/**
 * Sign Up Page using Clerk
 *
 * Uses Clerk's pre-built SignUp component with custom styling
 */

import { SignUp } from '@clerk/clerk-react';
import { DevTools } from '@/components/dev/DevTools';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { useEffect } from 'react';

export default function ClerkSignUp() {
  // Detect invite parameter and store for post-signup processing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteId = params.get('invite');
    if (inviteId) {
      sessionStorage.setItem('invite_tenant_id', inviteId);
      console.log('[SignUp] Stored invite tenant ID:', inviteId);
    }
  }, []);
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero Section - Hidden on mobile, left side on desktop */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-gradient-to-br from-primary via-primary/95 to-primary/90 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 py-12">
          {/* Logo */}
          <div
            className="flex items-center gap-3 mb-12 animate-fade-in-up animate-on-load"
            style={{ animationFillMode: 'forwards' }}
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/30">
              <Sparkles className="h-6 w-6" />
            </div>
            <span className="font-heading text-2xl font-bold text-white">RenovationAI</span>
          </div>

          {/* Hero Text */}
          <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-6">
            Start growing your
            <br />
            <span className="text-accent">renovation business</span>
          </h1>
          <p className="text-lg xl:text-xl text-white/80 max-w-md mb-8 leading-relaxed">
            Join contractors who are closing more deals with AI-powered quotes and stunning
            visualizations.
          </p>

          {/* Benefits List */}
          <div className="space-y-3">
            {['$99/month—no per-lead fees', 'Month-to-month—cancel anytime', 'Setup in under 5 minutes'].map(
              (benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 text-white/90 animate-fade-in-up animate-on-load"
                  style={{ animationDelay: `${150 + index * 100}ms`, animationFillMode: 'forwards' }}
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent/20">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                  </div>
                  <span className="text-base font-medium">{benefit}</span>
                </div>
              )
            )}
          </div>

          {/* Testimonial */}
          <div
            className="mt-10 relative animate-fade-in-up animate-on-load"
            style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/20 max-w-md">
              <p className="text-white/90 italic mb-3 text-[15px] leading-relaxed">
                "RenovationAI helped us close 40% more deals in our first month. The AI
                visualizations blow our clients away."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/30 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">MR</span>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Mike Rodriguez</p>
                  <p className="text-white/60 text-xs">Elite Home Renovations</p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-3xl" />
          </div>
        </div>
      </div>

      {/* Sign Up Form Section */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Mobile Header */}
        <div
          className="lg:hidden flex items-center justify-center gap-2.5 pt-8 pb-4 px-4 animate-fade-in-up"
          style={{ animationDuration: '0.5s' }}
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md shadow-primary/20">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-bold text-foreground">RenovationAI</span>
        </div>

        {/* Clerk SignUp Component */}
        <div className="flex-1 flex items-center justify-center px-5 py-4 sm:px-6 sm:py-8 lg:px-8 bg-background relative">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.02] via-transparent to-primary/[0.02]" />
          <SignUp
            routing="hash"
            signInUrl="/sign-in"
            forceRedirectUrl="/onboarding"
            appearance={{
              variables: {
                // BUG-004 FIX: Use Clerk's colorPrimary variable for button background
                // This overrides Clerk's internal styling that was making buttons transparent
                colorPrimary: '#148068', // hsl(166, 76%, 32%) - matches --accent
                colorPrimaryText: '#ffffff', // white text on buttons
              },
              elements: {
                rootBox: 'w-full max-w-[400px]',
                card: 'shadow-2xl shadow-primary/10 border-border/40',
                headerTitle: 'font-heading text-2xl sm:text-3xl font-bold',
                headerSubtitle: 'text-muted-foreground',
                socialButtonsBlockButton:
                  'h-11 sm:h-12 text-base font-medium border-border/60 hover:bg-muted/50',
                formFieldInput:
                  'h-11 sm:h-12 text-base px-4 bg-muted/30 border-border/50 focus:bg-background',
                formButtonPrimary:
                  'h-11 sm:h-12 text-base font-semibold shadow-md',
                footerActionLink: 'font-semibold text-accent hover:text-accent/80',
              },
            }}
          />
        </div>

        {/* Mobile Footer */}
        <div className="lg:hidden py-4 pb-6 text-center">
          <p className="text-xs text-muted-foreground">$99/month • Month-to-month • Cancel anytime</p>
        </div>
      </div>

      {/* Dev Tools */}
      <DevTools />
    </div>
  );
}
